/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Input from "components/Input";
import Select from "react-select";
import Button from "components/Button";
import apiService from "services/apiService";
import { sortByKey } from "util/sortArrByName";
import { validateNoTrailingOrLeadingWhitespace } from "util/validation";
import { handleNameInput } from "util/Util";
import { customSelectStyles } from "util/selectStyles";
import { UserRole } from "util/roles";
import PhoneNumberInput from "components/PhoneNumberInput";
import { DEFAULT_COUNTRY_CODE, joinPhoneNumber, phoneNumberSchema } from "util/phoneCountry";

const schema = yup.object().shape({
  emails: yup.string().required("Employees are required"),
  clientContactName: validateNoTrailingOrLeadingWhitespace("Client Name"),
  clientContactNumber: phoneNumberSchema("clientContactCountryCode", {
    requiredMessage: "Client Mobile Number is Required",
  }),
  clientContactCountryCode: yup.string().optional().default(DEFAULT_COUNTRY_CODE),
  manager: yup.string().required("Manager is Required"),
  name: validateNoTrailingOrLeadingWhitespace("Name").matches(
    /^[a-zA-Z0-9 ]*$/,
    "Special characters are not allowed"
  ),
  startDate: yup.string().required("Start Date is Required"),
  projectDetail: validateNoTrailingOrLeadingWhitespace("Project Details").max(
    1000,
    "Project Details cannot be more than 1000 characters"
  ),
  completionDate: yup.string().optional(),
});

interface AddProps {
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

interface OptionProps {
  value: string;
  label: string;
  role?: number;
}

type UserOptionSource = { email?: string; displayName?: string; role?: number };

const getProjectAddErrorMessage = (error: any) => {
  const status = error?.response?.status ?? error?.status;
  if (status === 500) return "Failed to add project";

  const responseData = error?.response?.data;
  if (typeof responseData === "string" && responseData.trim()) return responseData;
  if (typeof responseData?.message === "string" && responseData.message.trim()) return responseData.message;
  if (typeof error?.message === "string" && error.message.trim()) return error.message;
  if (typeof error?.statusText === "string" && error.statusText.trim()) return error.statusText;

  return "Failed to add project";
};

export default function AddProject({ onClose, onSuccess, onError }: AddProps) {
  const methods = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      name: "",
      manager: "",
      clientContactName: "",
      clientContactNumber: "",
      clientContactCountryCode: DEFAULT_COUNTRY_CODE,
      startDate: "",
      completionDate: "",
      emails: "",
      projectDetail: "",
    },
  });

  const { isDirty, isSubmitting } = methods.formState;

  const onSubmit: SubmitHandler<any> = (data: any) => {
    const { clientContactCountryCode, ...projectValues } = data;
    const cleanedData = {
      ...projectValues,
      clientContactNumber: joinPhoneNumber(clientContactCountryCode, data.clientContactNumber),
      projectDetail: data.projectDetail?.trim(),
    };
    apiService
      .post("api/project", cleanedData)
      .then((res) => {
        if (res.status === 200) {
          onSuccess("Project is Added successfully");
          onClose();
        } else {
          onError(getProjectAddErrorMessage(res));
        }
      })
      .catch((err) => {
        onError(getProjectAddErrorMessage(err));
      });
  };

  const [options, setOptions] = useState<OptionProps[]>([]);
  const [userPage, setUserPage] = useState(0);
  const [userSearch, setUserSearch] = useState("");
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const USER_PAGE_SIZE = 10;

  const [startDate, setStartDate] = useState("");
  const [completionDate, setCompletionDate] = useState("");
  const [, setWorkingDays] = useState(0);
  const onStartFromChanged = (e: any) => setStartDate(e.target.value);
  const onEndToChanged = (e: any) => setCompletionDate(e.target.value);

  useEffect(() => {
    if (startDate && completionDate) {
      const startFrom: Date = new Date(startDate);
      const completion: Date = new Date(completionDate);
      let days = 0;
      for (let d = new Date(startFrom); d <= completion; d.setDate(d.getDate() + 1)) {
        if (d.getDay() !== 6 && d.getDay() !== 0) days++;
      }
      setWorkingDays(days);
    }
  }, [startDate, completionDate]);

  const minLeaveToDate = startDate ? new Date(startDate).toISOString().split("T")[0] : "";

  const getUsersFromPayload = (payload: unknown): UserOptionSource[] => {
    if (Array.isArray(payload)) return payload as UserOptionSource[];
    if (typeof payload === "object" && payload !== null && "content" in payload && Array.isArray((payload as any).content)) {
      return (payload as { content: UserOptionSource[] }).content;
    }
    return [];
  };

  const dedupeAndSort = (items: OptionProps[]) => {
    const dedup = new Map<string, OptionProps>();
    items.forEach((item) => {
      const existing = dedup.get(item.value);
      if (!existing) {
        dedup.set(item.value, item);
      } else if (existing.label === existing.value && item.label !== item.value) {
        dedup.set(item.value, item);
      }
    });
    return sortByKey(Array.from(dedup.values()), "label");
  };

  const fetchUsers = async (page: number, searchValue: string, append: boolean) => {
    if (loadingUsers) return;
    setLoadingUsers(true);
    try {
      const query = new URLSearchParams();
      query.set("page", String(page));
      query.set("size", String(USER_PAGE_SIZE));
      if (searchValue.trim()) query.set("search", searchValue.trim());

      const res = await apiService.get(`api/users?${query.toString()}`);
      const payload = res.data;
      const users = getUsersFromPayload(payload);

      const mapped: OptionProps[] = users.map((user) => ({
        value: user.email || "",
        label: user.displayName || "",
        role: user.role,
      }));

      setOptions((prev) => dedupeAndSort(append ? [...prev, ...mapped] : mapped));

      setUserPage(page);
      if (typeof payload?.last === "boolean") {
        setHasMoreUsers(!payload.last);
      } else if (typeof payload?.totalPages === "number") {
        setHasMoreUsers(page < payload.totalPages - 1);
      } else {
        setHasMoreUsers(mapped.length === USER_PAGE_SIZE);
      }
    } catch (err) {
      console.log("err", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void fetchUsers(0, userSearch, false);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [userSearch]);

  const getSelectOptionClass = (isFocused: boolean, isSelected: boolean) => {
    if (isSelected) return "!text-sm !bg-primary !text-white";
    if (isFocused) return "!text-sm !bg-primary/10 !text-primary";
    return "!text-slate-700 dark:!text-slate-300";
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 px-4 pt-3">
          <Input compact name="name" label="Project Name" placeholder="Enter Project Name" required />

          <div className="mb-0">
            <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
              Select Manager <span className="text-error">*</span>
            </label>
            <Select
              styles={customSelectStyles}
              {...methods.register("manager", { required: true })}
              options={options.filter(opt => opt.role === UserRole.MANAGER || UserRole.ADMIN && opt.value !== "")}
              isLoading={loadingUsers}
              placeholder="Select Manager"
              className="w-full"
              onMenuScrollToBottom={() => {
                if (hasMoreUsers && !loadingUsers) void fetchUsers(userPage + 1, userSearch, true);
              }}
              onInputChange={(value, actionMeta) => {
                if (actionMeta.action === "input-change") setUserSearch(value);
                if (actionMeta.action === "menu-close") setUserSearch("");
                return value;
              }}
              classNames={{
                control: ({ isFocused }) => `!rounded-lg !border-gray-300 dark:!border-gray-600 !bg-gray-50 dark:!bg-gray-700 !min-h-[42px] ${isFocused ? '!ring-2 !ring-primary/20 !border-primary' : ''}`,
                placeholder: () => "!text-slate-400 dark:!text-gray-400 !text-sm",
                singleValue: () => "!text-slate-700 dark:!text-slate-200 !text-sm",
                menu: () => "!bg-white dark:!bg-gray-800 !border !border-slate-200 dark:!border-slate-700 !rounded-xl !shadow-xl !overflow-hidden",
                option: ({ isFocused, isSelected }) => `!text-sm ${getSelectOptionClass(isFocused, isSelected)}`
              }}
              onChange={(selectedOption: any) => {
                methods.setValue("manager", selectedOption?.value || "", {
                  shouldValidate: true,
                  shouldDirty: true,
                  shouldTouch: true,
                });
              }}
            />
            {methods.formState.errors.manager && (
              <span className="text-error text-xs mt-1 block font-medium">
                {methods.formState.errors.manager.message as React.ReactNode}
              </span>
            )}
          </div>

          <Input compact name="clientContactName" label="Client Name" placeholder="Enter Client Name" required onInput={(e: React.FormEvent<HTMLInputElement>) =>
            handleNameInput(e, methods.setValue)
          } />
          <PhoneNumberInput
            compact
            name="clientContactNumber"
            countryCodeName="clientContactCountryCode"
            label="Client Mobile Number"
            placeholder="Enter Mobile Number"
            required
          />
          <Input compact name="startDate" label="Start Date" type="date" value={startDate} onChange={onStartFromChanged} required />
          <Input compact name="completionDate" label="Completion Date" type="date" value={completionDate} disabled={!startDate} onChange={onEndToChanged} min={minLeaveToDate} />
        </div>

        <div className="px-4 mt-2">
          <label className="block mb-1 text-sm font-semibold">
            Select Employee <span className="text-error">*</span>
          </label>
          <div className="w-full">
            <Select
              styles={customSelectStyles}
              isMulti
              {...methods.register("emails", { required: true })}
              options={options.filter(opt => opt.value !== "")}
              isLoading={loadingUsers}
              placeholder="Select Employees"
              className="w-full"
              onMenuScrollToBottom={() => {
                if (hasMoreUsers && !loadingUsers) void fetchUsers(userPage + 1, userSearch, true);
              }}
              onInputChange={(value, actionMeta) => {
                if (actionMeta.action === "input-change") setUserSearch(value);
                if (actionMeta.action === "menu-close") setUserSearch("");
                return value;
              }}
              classNames={{
                control: ({ isFocused }) => `!rounded-lg !border-gray-300 dark:!border-gray-600 !bg-gray-50 dark:!bg-gray-700 !min-h-[42px] ${isFocused ? "!ring-2 !ring-primary/20 !border-primary" : ""}`,
                placeholder: () => "!text-slate-400 dark:!text-gray-400 !text-sm",
                multiValue: () => "!bg-primary/10 !text-primary !rounded-lg !border !border-primary/20",
                multiValueLabel: () => "!text-primary !text-xs !font-semibold",
                multiValueRemove: () => "!text-primary hover:!bg-primary/20 !rounded-r-lg",
                menu: () => "!bg-white dark:!bg-gray-800 !border !border-slate-200 dark:!border-slate-700 !rounded-xl !shadow-xl !overflow-hidden",
                option: ({ isFocused, isSelected }) => `!text-sm ${getSelectOptionClass(isFocused, isSelected)}`,
              }}
              onChange={(selectedOptions: any) => {
                const value = (selectedOptions || []).map((opt: any) => opt.value).join(",");
                methods.setValue("emails", value, {
                  shouldValidate: true,
                  shouldDirty: true,
                  shouldTouch: true,
                });
              }}
            />
            {methods.formState.errors.emails && (
              <span className="text-error text-xs mt-1 block font-medium">
                {methods.formState.errors.emails.message as React.ReactNode}
              </span>
            )}
          </div>
        </div>

        <div className="px-4 mt-1">
          <Input placeholder="Project Detail" compact name="projectDetail" label="Project Detail" type="textarea" required />
        </div>
        <div className="mt-4 flex justify-end px-2 gap-2">
          <Button
            type="button"
            onClick={onClose}
            className="!bg-white !text-slate-600 border border-slate-200 hover:!bg-slate-100 focus:!ring-4 focus:!outline-none focus:!ring-slate-200 transition-all active:scale-95"
          >
            Cancel
          </Button>
          <Button
          disabled={!isDirty || isSubmitting}
            type="submit"
            className="!text-white !bg-blue-700 hover:!bg-blue-800 focus:!ring-4 focus:!outline-none focus:!ring-blue-300 dark:!bg-blue-600 dark:hover:!bg-blue-700 dark:focus:!ring-blue-800"
          >
            Submit
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}